
import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../components/AppIcon';
import JSZip from 'jszip';
import {
  guardarArchivosPeso,
  guardarEstadoPeso,
  recuperarSesionPeso,
  borrarSesionPeso
} from './pesoSessionStorage';
import { useNotification } from '../../components/ui/NotificationProvider';
import { supabase } from '../../lib/supabase';

// PESO_DIMENSIONES_SOLO_V1
const CAMPOS = [
  'pesoArticulo', 'dimensionesArticulo',
  'dimensionesParcialesArticulo',
  'pesoPaquete', 'dimensionesPaquete'
];

const asinDe = p => String(
  p?.asin || p?.amazon_asin || ''
).trim().toUpperCase();

const esperar = ms => new Promise(r => setTimeout(r, ms));

// PESO_ENVIO_LIGERO_V9
// Solo se transmite la información logística necesaria.
// Los JSON cargados y descargados permanecen completos.
const CLAVE_MEDIDA = /weight|dimensions?|size|peso|dimensi[oó]n|package|shipping|paquete/i;

function productoLogisticoReducido(producto, asin) {
  const salida = { asin };

  const campos = [
    'packageWeight', 'package_weight', 'shippingWeight',
    'packageDimensions', 'package_dimensions',
    'shippingDimensions', 'itemWeight', 'item_weight',
    'productDimensions', 'product_dimensions'
  ];

  for (const campo of campos) {
    const valor = producto?.[campo];

    if (
      (typeof valor === 'string' && valor.length <= 250) ||
      (typeof valor === 'number' && Number.isFinite(valor))
    ) {
      salida[campo] = valor;
    }
  }

  const logistica = producto?.mcoLogistics;

  if (logistica && typeof logistica === 'object') {
    salida.mcoLogistics = {};

    for (const campo of [
      'pesoArticulo', 'dimensionesArticulo',
      'dimensionesParcialesArticulo',
      'pesoPaquete', 'dimensionesPaquete'
    ]) {
      if (logistica[campo]) {
        salida.mcoLogistics[campo] = logistica[campo];
      }
    }
  }

  for (const nombre of [
    'productOverview',
    'amazonAttributesRaw',
    'attributes',
    'technicalAttributes',
    'manufacturerAttributes',
    'technicalDetails',
    'productDetails',
    'specifications'
  ]) {
    const seccion = producto?.[nombre];

    if (Array.isArray(seccion)) {
      const medidas = [];

      for (const item of seccion) {
        const key = String(
          item?.key || item?.name ||
          item?.label || item?.id || ''
        ).trim();

        if (!CLAVE_MEDIDA.test(key)) continue;

        const valor =
          item?.value ??
          item?.value_name ??
          item?.valueName ??
          item?.displayValue ??
          item?.text;

        if (
          typeof valor !== 'string' &&
          typeof valor !== 'number'
        ) continue;

        medidas.push({
          key: key.slice(0, 100),
          value: String(valor).slice(0, 250)
        });

        if (medidas.length >= 20) break;
      }

      if (medidas.length) salida[nombre] = medidas;

    } else if (
      seccion &&
      typeof seccion === 'object'
    ) {
      const medidas = {};

      for (const [key, value] of Object.entries(seccion)) {
        if (!CLAVE_MEDIDA.test(key)) continue;

        if (
          typeof value !== 'string' &&
          typeof value !== 'number'
        ) continue;

        medidas[key] = String(value).slice(0, 250);

        if (Object.keys(medidas).length >= 20) break;
      }

      if (Object.keys(medidas).length) {
        salida[nombre] = medidas;
      }
    }
  }

  return salida;
}

async function solicitar(ruta, datos) {
  const { data: { session } } =
    await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('La sesión de usuario ha expirado.');
  }

  let respuesta;

  try {
    respuesta = await fetch(
      '/api/admin/mco-classifier/' + ruta,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(datos)
      }
    );
  } catch (error) {
    throw new Error(
      'No se pudo conectar con el servidor de Mercado Bulk.'
    );
  }

  let json;

  try {
    json = await respuesta.json();
  } catch (_) {
    if (respuesta.status === 413) {
      throw new Error(
        'El servidor rechazó una solicitud demasiado grande.'
      );
    }

    throw new Error(
      'El servidor devolvió una respuesta no válida.'
    );
  }

  if (!respuesta.ok || !json?.success) {
    const mensaje = String(json?.message || '');

    if (
      respuesta.status === 413 ||
      /request entity too large|payload too large/i.test(mensaje)
    ) {
      throw new Error(
        'El servidor rechazó una solicitud demasiado grande.'
      );
    }

    if (
      /fetch failed|failed to fetch|networkerror/i.test(mensaje)
    ) {
      throw new Error(
        'No se pudo establecer conexión con el servidor.'
      );
    }

    throw new Error(
      mensaje || 'No se pudo procesar la solicitud.'
    );
  }

  return json.data;
}

const peso = p => p?.grams > 0
  ? `${p.grams} g` : 'Pendiente';

const dimensiones = d => d?.lengthCm > 0
  ? `${Number(Number(d.lengthCm).toFixed(2))} × ${Number(Number(d.widthCm).toFixed(2))} × ${Number(Number(d.heightCm).toFixed(2))} cm`
  : 'Pendiente';

const paqueteCompleto = l =>
  l?.pesoPaquete?.grams > 0 &&
  l?.dimensionesPaquete?.lengthCm > 0 &&
  l?.dimensionesPaquete?.widthCm > 0 &&
  l?.dimensionesPaquete?.heightCm > 0;


// PESO_ESTADOS_CORRECTOS_V11
// Criterio único para estado, filtros y contadores.
const completoParaVista = l => {
  const pesoValido =
    Number(l?.pesoPaquete?.grams) > 0 ||
    Number(l?.pesoArticulo?.grams) > 0;

  const dimensionesValidas = [
    l?.dimensionesPaquete,
    l?.dimensionesArticulo
  ].some(d =>
    Number(d?.lengthCm) > 0 &&
    Number(d?.widthCm) > 0 &&
    Number(d?.heightCm) > 0
  );

  return pesoValido && dimensionesValidas;
};


// PESO_COMPATIBLE_PRECIOS_V12
// Prepara los campos que reconoce el motor de precios.
// No reemplaza pesos ni dimensiones existentes.
function prepararLogisticaParaPrecios(producto) {
  if (!producto || typeof producto !== 'object') return;

  const l = producto.mcoLogistics || {};

  const pesoPaquete = Number(l.pesoPaquete?.grams);
  const pesoArticulo = Number(l.pesoArticulo?.grams);

  const gramos = pesoPaquete > 0
    ? pesoPaquete
    : pesoArticulo > 0
      ? pesoArticulo
      : null;

  const dimensionesValidas = d =>
    d &&
    Number(d.lengthCm) > 0 &&
    Number(d.widthCm) > 0 &&
    Number(d.heightCm) > 0;

  const dimensiones = dimensionesValidas(l.dimensionesPaquete)
    ? l.dimensionesPaquete
    : dimensionesValidas(l.dimensionesArticulo)
      ? l.dimensionesArticulo
      : null;

  if (
    gramos &&
    !(Number(producto.billableWeightLb) > 0) &&
    !(Number(producto.billable_weight_lb) > 0)
  ) {
    producto.billableWeightLb =
      Number((gramos / 453.59237).toFixed(6));
  }

  if (
    gramos &&
    dimensiones &&
    !producto.mercadoLibreDimensions &&
    !producto.mercado_libre_dimensions
  ) {
    const alto = Math.ceil(Number(dimensiones.heightCm));
    const ancho = Math.ceil(Number(dimensiones.widthCm));
    const largo = Math.ceil(Number(dimensiones.lengthCm));
    const peso = Math.ceil(gramos);

    producto.mercadoLibreDimensions =
      `${alto}x${ancho}x${largo},${peso}`;
  }
}


// PESO_ESTIMACIONES_AUTOMATICAS_V13
// Estima solamente el dato faltante.
// Nunca sustituye pesos o dimensiones válidos.
function estimarMedidasMcoV13(producto) {
  if (!producto || typeof producto !== 'object') return false;

  const l = producto.mcoLogistics || {};

  const numeroValido = valor => {
    const n = Number(valor);
    return Number.isFinite(n) && n > 0;
  };

  const dimensionesValidas = d =>
    numeroValido(d?.lengthCm) &&
    numeroValido(d?.widthCm) &&
    numeroValido(d?.heightCm);

  const pesoPaqueteValido = numeroValido(l.pesoPaquete?.grams);
  const pesoArticuloValido = numeroValido(l.pesoArticulo?.grams);

  const dimensionesPaqueteValidas =
    dimensionesValidas(l.dimensionesPaquete);

  const dimensionesArticuloValidas =
    dimensionesValidas(l.dimensionesArticulo);

  const tienePeso = pesoPaqueteValido || pesoArticuloValido;

  const tieneDimensiones =
    dimensionesPaqueteValidas || dimensionesArticuloValidas;

  if (tienePeso && tieneDimensiones) return false;

  // Sin ninguno de los dos datos no existe base matemática.
  if (!tienePeso && !tieneDimensiones) return false;

  producto.mcoLogistics = l;

  // CASO A: Existen dimensiones, pero no peso.
  if (!tienePeso && tieneDimensiones) {
    const esPaquete = dimensionesPaqueteValidas;

    const d = esPaquete
      ? l.dimensionesPaquete
      : l.dimensionesArticulo;

    const volumen =
      Number(d.lengthCm) *
      Number(d.widthCm) *
      Number(d.heightCm);

    if (!numeroValido(volumen)) return false;

    // Factor volumétrico 5000 cm³/kg.
    const gramos = Math.max(
      1,
      Math.ceil(volumen / 5)
    );

    const campo = esPaquete
      ? 'pesoPaquete'
      : 'pesoArticulo';

    l[campo] = {
      raw: `${gramos} g (estimado)`,
      grams: gramos,
      pounds: gramos / 453.59237,
      estimado: true,
      fuente: 'estimacion_matematica',
      metodo: 'peso_volumetrico_5000'
    };

    return true;
  }


  // PESO_2D_CON_PESO_V14
  // Se conservan las dos medidas originales.
  // Los ejes se asignan provisionalmente, no se verifican.
  if (
    !tieneDimensiones &&
    pesoArticuloValido &&
    !pesoPaqueteValido &&
    !l.dimensionesArticulo
  ) {
    const parcial = l.dimensionesParcialesArticulo;
    const a = Number(parcial?.medida1Cm);
    const b = Number(parcial?.medida2Cm);

    if (numeroValido(a) && numeroValido(b)) {
      const volumen = Number(l.pesoArticulo.grams) * 5;
      const alto = Math.max(1, Math.ceil(volumen / (a * b)));

      if (numeroValido(alto)) {
        l.dimensionesArticulo = {
          raw: `${parcial.raw} + ${alto} cm (tercera medida estimada)`,
          lengthCm: a,
          widthCm: b,
          heightCm: alto,
          estimado: true,
          fuente: 'amazon_2d_mas_estimacion',
          metodo: 'volumetrico_5000_inverso',
          ejesInferidos: true,
          medidasReales2D: {
            medida1Cm: a,
            medida2Cm: b
          }
        };

        return true;
      }
    }
  }

  // CASO B: Existe peso, pero faltan dimensiones.
  if (tienePeso && !tieneDimensiones) {
    const esPaquete = pesoPaqueteValido;

    const gramos = esPaquete
      ? Number(l.pesoPaquete.grams)
      : Number(l.pesoArticulo.grams);

    const volumen = gramos * 5;

    if (!numeroValido(volumen)) return false;

    // Caja hipotética de proporciones 1,5 : 1 : 0,5.
    // El divisor 0,75 mantiene el volumen supuesto.
    const base = Math.cbrt(volumen / 0.75);

    const largo = Math.max(
      15,
      Math.ceil(base * 1.5)
    );

    const ancho = Math.max(
      10,
      Math.ceil(base)
    );

    const alto = Math.max(
      2,
      Math.ceil(base / 2)
    );

    const campo = esPaquete
      ? 'dimensionesPaquete'
      : 'dimensionesArticulo';

    l[campo] = {
      raw: `${largo} x ${ancho} x ${alto} cm (estimadas)`,
      lengthCm: largo,
      widthCm: ancho,
      heightCm: alto,
      estimado: true,
      fuente: 'estimacion_matematica',
      metodo: 'caja_proporcional_volumetrica'
    };

    return true;
  }

  return false;
}

export default function PesoYDimensiones() {
  const { showInfo, showSuccess, showWarning, showError, removeNotification, updateNotification } =
    useNotification();
  const ultimoError = useRef('');
  // PESO_SESION_SEGURA_V8
  const archivos = useRef([]);
  const usuarioPeso = useRef(null);
  const origenes = useRef(new WeakMap());
  const sucios = useRef(new Set());
  const terminadosRef = useRef(0);
  const [sesionLista, setSesionLista] = useState(false);
  const productos = useRef(new Map());
  const estados = useRef(new Map());
  // MCO_AVANCE_ACUMULADO_V1
  const atendidosHistoricos = useRef(new Set());
  const activo = useRef(false);
  const detenido = useRef(false);
  const lote = useRef(null);
  const cursor = useRef(0);
  const consultando = useRef(false);

  const [, actualizarVista] = useState(0);
  // PESO_COLA_ARCHIVOS_V6_CORREGIDO
  const [procesando, setProcesando] = useState(false);
  // MCO_ALERTA_CARGA_REAL_V1
  const [cargando, setCargando] = useState(false);
  const cargandoRef = useRef(false);
  const archivoActual = useRef(null);
  const enviadosActuales = useRef(new Set());
  const [archivoActivo, setArchivoActivo] = useState('');
  const [archivosTerminados, setArchivosTerminados] = useState(0);
  const [exportando, setExportando] = useState(false);
  const [mensaje, setMensaje] = useState('Carga tus JSON o ZIP.');
  const [enviados, setEnviados] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [filtro,setFiltro]=useState('todos');
  const [seleccionados,setSeleccionados]=useState([]);
  // PESO_FOTOS_PROGRESO_V3
  const [imagenAmpliada,setImagenAmpliada]=useState(null);

  const registrarAvance = () => {
    for (const [asin, estado] of estados.current) {
      if (['done', 'completo', 'failed', 'cancelled']
          .includes(estado)) {
        atendidosHistoricos.current.add(asin);
      }
    }
  };

  const actualizar = () => {
    registrarAvance();
    actualizarVista(n => n + 1);
  };

  async function guardarSesionActual() {
    const uid = usuarioPeso.current;
    if (!uid) return;

    const nombres = [...sucios.current];
    nombres.forEach(n => sucios.current.delete(n));

    const lista = archivos.current.filter(
      a => nombres.includes(a.nombre)
    );

    try {
      await guardarArchivosPeso(uid, lista);

      registrarAvance();
      await guardarEstadoPeso(uid, {
        atendidosHistoricos: [...atendidosHistoricos.current],
        estados: [...estados.current.entries()],
        enviados: [...enviadosActuales.current],
        runId: lote.current,
        archivosTerminados: terminadosRef.current
      });
    } catch (e) {
      nombres.forEach(n => sucios.current.add(n));
      throw e;
    }
  }

  async function recargarSesion(silencio = false) {
    if (activo.current) return;

    setSesionLista(false);

    try {
      const { data: { session } } =
        await supabase.auth.getSession();

      const uid = session?.user?.id;

      if (!uid) {
        throw new Error(
          'No hay una sesión de usuario activa'
        );
      }

      usuarioPeso.current = uid;

      const { archivos: recuperados, estado } =
        await recuperarSesionPeso(uid);

      const lista = [];
      const mapa = new Map();
      const origen = new WeakMap();

      for (const a of recuperados) {
        const estructura = a.estructura;

        const productosArchivo = Array.isArray(estructura)
          ? estructura
          : Array.isArray(estructura?.products)
            ? estructura.products
            : estructura?.items;

        if (!Array.isArray(productosArchivo)) {
          continue;
        }

        lista.push({
          nombre: a.nombre,
          estructura,
          lista: productosArchivo
        });

        for (const producto of productosArchivo) {
          const asin = asinDe(producto);

          if (!/^[A-Z0-9]{10}$/.test(asin)) {
            continue;
          }

          const arr = mapa.get(asin) || [];
          arr.push(producto);
          mapa.set(asin, arr);
          origen.set(producto, a.nombre);
        }
      }

      archivos.current = lista;
      productos.current = mapa;
      origenes.current = origen;
      sucios.current.clear();

      estados.current = new Map(
        estado?.estados || []
      );

      enviadosActuales.current = new Set(
        estado?.enviados || []
      );

      atendidosHistoricos.current = new Set(
        Array.isArray(estado?.atendidosHistoricos)
          ? estado.atendidosHistoricos
          : []
      );
      registrarAvance();

      lote.current = estado?.runId || null;

      terminadosRef.current = Number(
        estado?.archivosTerminados || 0
      );

      setEnviados(enviadosActuales.current.size);
      setArchivosTerminados(terminadosRef.current);
      setProcesando(false);
      setArchivoActivo('');
      setPagina(1);

      setMensaje(
        lista.length
          ? `Sesión recuperada: ${lista.length} archivos.`
          : 'Carga tus JSON o ZIP.'
      );

      actualizar();

      if (!silencio) {
        showSuccess(
          `Recuperados ${lista.length} archivos.`,
          { title: 'Sesión recuperada' }
        );
      }

    } catch (e) {
      setMensaje(
        'No se pudo recuperar la sesión: ' + e.message
      );

      if (!silencio) {
        showError(e.message, {
          title: 'Recargar sesión'
        });
      }
    } finally {
      setSesionLista(true);
    }
  }

  useEffect(() => {
    recargarSesion(true);
  }, []);

  useEffect(() => {
    if (!sesionLista || activo.current || !productos.current.size) {
      return;
    }

    reconocerOriginalesSinScrapear()
      .then(() => guardarSesionActual())
      .catch(error => {
        console.warn('[MCO] Prevalidación:', error.message);
      });
  }, [sesionLista]);

  function incorporar(asin, logistica, estado) {
    for (const producto of productos.current.get(asin) || []) {
      if (!logistica) continue;

      const datos = { ...(producto.mcoLogistics || {}) };

      for (const campo of CAMPOS) {
        if (!datos[campo] && logistica[campo]) {
          datos[campo] = logistica[campo];
        }
      }

      producto.mcoLogistics = datos;
      prepararLogisticaParaPrecios(producto);

      const nombre = origenes.current.get(producto);

      if (nombre) {
        sucios.current.add(nombre);
      }

      if (
        !producto.mercadoLibreDimensions &&
        !producto.mercado_libre_dimensions &&
        paqueteCompleto(datos)
      ) {
        const d = datos.dimensionesPaquete;

        producto.mercadoLibreDimensions =
          `${Math.ceil(d.heightCm)}x` +
          `${Math.ceil(d.widthCm)}x` +
          `${Math.ceil(d.lengthCm)},${datos.pesoPaquete.grams}`;
      }
    }

    // MCO_CONSERVAR_TERMINADOS_REALES_V1
    // Registrar un resultado final antes de cualquier reintento.
    if (estado) {
      const previo = estados.current.get(asin);
      const esFinal = valor =>
        ['done', 'completo', 'failed', 'cancelled'].includes(valor);

      if (esFinal(previo) || esFinal(estado)) {
        atendidosHistoricos.current.add(asin);
      }

      estados.current.set(asin, estado);
    }
  }


  // MCO_PREVALIDACION_ORIGINALES_V1
  async function reconocerOriginalesSinScrapear(alProgreso = null) {
    const lista = [];

    for (const [asin, asociados] of productos.current) {
      for (const p of asociados) {
        lista.push(productoLogisticoReducido(p, asin));
      }
    }

    for (let i = 0; i < lista.length; i += 100) {
      const res = await solicitar('weight-batch', {
        preflight: true,
        products: lista.slice(i, i + 100)
      });

      for (const item of res.results || []) {
        if (item.logistica) {
          incorporar(item.asin, item.logistica, null);
        }
      }

      alProgreso?.(
        Math.min(i + 100, lista.length),
        lista.length
      );
    }

    let completos = 0;

    for (const [asin, asociados] of productos.current) {
      if (
        asociados.length &&
        asociados.every(p => completoParaVista(p.mcoLogistics))
      ) {
        estados.current.set(asin, 'completo');
        completos++;
      }
    }

    actualizar();
    return completos;
  }

  function nuevoLote() {
    if (activo.current || cargandoRef.current) return;

    archivos.current = [];
    origenes.current = new WeakMap();
    sucios.current.clear();
    terminadosRef.current = 0;

    const uid = usuarioPeso.current;

    if (uid) {
      borrarSesionPeso(uid).catch(e =>
        showError(e.message, {
          title: 'Error limpiando sesión'
        })
      );
    }
    productos.current = new Map();
    estados.current = new Map();
    atendidosHistoricos.current = new Set();
    enviadosActuales.current = new Set();
    archivoActual.current = null;
    lote.current = null;
    cursor.current = 0;

    setEnviados(0);
    setArchivosTerminados(0);
    setArchivoActivo('');
    setSeleccionados([]);
    setPagina(1);
    setBusqueda('');
    setFiltro('todos');
    setMensaje('Carga tus JSON o ZIP.');
    actualizar();
  }

  async function cargar(evento) {
    if (!sesionLista || cargandoRef.current) return;

    cargandoRef.current = true;
    setCargando(true);

    const alertaId = showInfo(
      'Preparando archivos...',
      {
        title: 'Cargando y verificando',
        persistent: true,
        duration: 0
      }
    );

    const mostrar = (actual, total, etapa) => {
      const porcentaje = total > 0
        ? Math.min(100, Math.round(actual * 100 / total))
        : 0;

      const frames = ['◜', '◠', '◝', '◞', '◡', '◟'];
      const frame = frames[actual % frames.length];

      updateNotification(alertaId, {
        message:
          `${frame} ${etapa}: ${actual} / ${total}\n` +
          `${porcentaje}%`
      });
    };

    try {
      const nuevos = [];
      const nombres = new Set(
        archivos.current.map(x => x.nombre)
      );

      function agregar(nombre, estructura) {
        const lista = Array.isArray(estructura)
          ? estructura
          : Array.isArray(estructura?.products)
            ? estructura.products
            : estructura?.items;

        if (!Array.isArray(lista)) {
          throw Error('JSON inválido: ' + nombre);
        }

        if (nombres.has(nombre)) {
          throw Error('Archivo repetido: ' + nombre);
        }

        nombres.add(nombre);

        nuevos.push({
          nombre,
          estructura,
          lista
        });
      }

      for (const archivo of Array.from(evento.target.files || [])) {
        if (/\.zip$/i.test(archivo.name)) {
          const zip = await JSZip.loadAsync(archivo);

          const entradas = Object.values(zip.files)
            .filter(item =>
              !item.dir && /\.json$/i.test(item.name)
            );

          for (let j = 0; j < entradas.length; j++) {
            const item = entradas[j];

            agregar(
              item.name,
              JSON.parse(await item.async('string'))
            );

            mostrar(j + 1, entradas.length, 'Leyendo JSON');
          }
        } else if (/\.json$/i.test(archivo.name)) {
          agregar(
            archivo.name,
            JSON.parse(await archivo.text())
          );
        } else {
          throw Error('Solo se admiten JSON y ZIP');
        }
      }

      for (const archivo of nuevos) {
        for (const producto of archivo.lista) {
          const asin = asinDe(producto);

          if (!/^[A-Z0-9]{10}$/.test(asin)) continue;

          const asociados = productos.current.get(asin) || [];
          asociados.push(producto);
          origenes.current.set(
            producto,
            archivo.nombre
          );
          productos.current.set(asin, asociados);

          if (completoParaVista(producto.mcoLogistics)) {
            estados.current.set(asin, 'completo');
          }
        }
      }

      // Preparar los productos originales para el motor de precios.
      for (const archivo of nuevos) {
        for (const producto of archivo.lista || []) {
          prepararLogisticaParaPrecios(producto);
        }
      }

      // MCO_CARGA_VISIBLE_V2
      // Mostrar los archivos antes de iniciar la prevalidación.
      archivos.current.push(...nuevos);

      nuevos.forEach(a =>
        sucios.current.add(a.nombre)
      );

      setPagina(1);
      setMensaje(
        `${nuevos.length} archivos JSON cargados. ` +
        'Reconociendo datos originales...'
      );
      actualizar();

      // Guardar primero los archivos originales.
      await guardarSesionActual();

      // Después reconocer pesos y dimensiones existentes.
      try {
        await reconocerOriginalesSinScrapear(
          (actual, total) =>
            mostrar(actual, total, 'Verificando productos')
        );
        await guardarSesionActual();

        setMensaje(
          `${nuevos.length} archivos JSON cargados y verificados.`
        );
        actualizar();

        showSuccess(
          `${nuevos.length} JSON verificados. Ya puedes iniciar.`,
          {
            title: 'Carga finalizada',
            duration: 5000
          }
        );
      } catch (error) {
        setMensaje(
          'Archivos cargados, pero falló la prevalidación: ' +
          error.message
        );
        showError(error.message, {
          title: 'Prevalidación logística'
        });
      }

    } catch (error) {
      setMensaje('Error de carga: ' + error.message);
      showError(error.message, {
        title: 'Error cargando archivos'
      });
    } finally {
      cargandoRef.current = false;
      setCargando(false);
      evento.target.value = '';
      removeNotification(alertaId);
    }
  }


  async function sincronizar() {
    if (!activo.current || consultando.current) return;

    consultando.current = true;

    try {
      const pendientes = [...enviadosActuales.current].filter(
        asin => !['done', 'completo', 'failed', 'cancelled']
          .includes(estados.current.get(asin))
      );

      for (let i = 0; i < pendientes.length; i += 200) {
        if (detenido.current) return;

        const respuesta = await solicitar('weight-results', {
          asins: pendientes.slice(i, i + 200)
        });

        if (detenido.current) return;

        for (const item of respuesta.results || []) {
          incorporar(item.asin, item.logistica, item.status);
        }
      }

      await guardarSesionActual();
      actualizar();

    } catch (error) {
      setMensaje('Error consultando resultados: ' + error.message);

      if (ultimoError.current !== error.message) {
        ultimoError.current = error.message;
        showError(error.message, {
          title: 'Error consultando el i3'
        });
      }
    } finally {
      consultando.current = false;
    }
  }

  useEffect(() => {
    const temporizador = setInterval(() => {
      if (activo.current) sincronizar();
    }, 3000);

    return () => clearInterval(temporizador);
  }, []);


  async function iniciar() {
    if (activo.current || cargandoRef.current || !productos.current.size) return;

    try {
      setMensaje('Reconociendo datos originales...');
      const completos = await reconocerOriginalesSinScrapear();
      await guardarSesionActual();
      setMensaje(
        `${completos} ASIN completos desde el inicio.`
      );
    } catch (error) {
      setMensaje('Error verificando originales: ' + error.message);
      showError(error.message, {
        title: 'Prevalidación logística'
      });
      return;
    }

    activo.current = true;
    detenido.current = false;
    // MCO_REANUDACION_SEGURA_V1
    // Crear identificador para nuevos trabajos sin borrar estados recuperados.
    lote.current = window.crypto.randomUUID();

    // MCO_CONTROL_I3_POR_INICIO_V1
    // El VPS autoriza esta ejecución antes de enviar trabajos.
    try {
      await solicitar('weight-start', {
        runId: lote.current
      });
    } catch (error) {
      activo.current = false;
      detenido.current = true;
      setProcesando(false);

      setMensaje(
        'No se pudo iniciar el scraper: ' + error.message
      );

      showError(error.message, {
        title: 'Error iniciando scraper'
      });

      return;
    }

    for (const [asin, asociados] of productos.current) {
      if (
        asociados.length > 0 &&
        asociados.every(p => completoParaVista(p.mcoLogistics))
      ) {
        estados.current.set(asin, 'completo');
      }
    }

    // Conservar ASIN enviados en la sesión recuperada.
    archivoActual.current = null;
    ultimoError.current = '';

    setProcesando(true);
    setEnviados(enviadosActuales.current.size);
    setArchivosTerminados(terminadosRef.current);
    setArchivoActivo('');
    setMensaje('Recuperación iniciada.');

    // Recuperar trabajos anteriores antes de enviar nuevos a Amazon.
    try {
      const asinsGuardados = [...productos.current.keys()];

      for (let i = 0; i < asinsGuardados.length; i += 200) {
        const respuesta = await solicitar('weight-results', {
          asins: asinsGuardados.slice(i, i + 200)
        });

        for (const item of respuesta.results || []) {
          if (item.logistica) {
            incorporar(item.asin, item.logistica, item.status);
          }
        }
      }

      actualizar();
    } catch (error) {
      console.warn(
        '[MCO] No se pudieron recuperar todos los resultados previos:',
        error
      );
    }


    showInfo('Comenzó el procesamiento de archivos.', {
      title: 'Proceso iniciado',
      duration: 12000
    });

    actualizar();

    try {
      await guardarSesionActual();

      // MCO_REANUDAR_DESDE_ARCHIVO_PENDIENTE_V1
      // No volver a recorrer archivos ya terminados.
      for (
        let indice = Math.min(
          Math.max(0, Number(terminadosRef.current) || 0),
          archivos.current.length
        );
        indice < archivos.current.length;
        indice++
      ) {
        if (detenido.current) break;

        const archivo = archivos.current[indice];
        archivoActual.current = archivo;
        setArchivoActivo(archivo.nombre);

        const productosArchivo = new Map();

        for (const producto of archivo.lista) {
          const asin = asinDe(producto);
          if (/^[A-Z0-9]{10}$/.test(asin)) {
            if (!productosArchivo.has(asin)) {
              productosArchivo.set(asin, producto);
            }
          }
        }

        const asins = [...productosArchivo.keys()].filter(asin =>
          (productos.current.get(asin) || []).some(
            p => !completoParaVista(p.mcoLogistics)
          )
        );

        setMensaje(
          `Procesando archivo ${indice + 1} de ` +
          `${archivos.current.length}: ${archivo.nombre}`
        );

        for (let i = 0; i < asins.length; i += 25) {
          if (detenido.current) break;

          const parte = asins.slice(i, i + 25);

          const respuesta = await solicitar('weight-batch', {
            runId: lote.current,
            products: parte.map(asin => {
              const asociados = productos.current.get(asin) || [];
              const completo = asociados.find(p =>
                completoParaVista(p.mcoLogistics)
              );

              return productoLogisticoReducido(
                completo || productosArchivo.get(asin), asin
              );
            })
          });

          if (detenido.current) break;

          for (const item of respuesta.results || []) {
            incorporar(item.asin, item.logistica, item.status);
          }

          for (const asin of parte) {
            enviadosActuales.current.add(asin);
          }

          setEnviados(enviadosActuales.current.size);
          await guardarSesionActual();
          actualizar();
        }

        while (!detenido.current && asins.some(
          asin => !['done', 'completo', 'failed', 'cancelled']
            .includes(estados.current.get(asin))
        )) {
          await sincronizar();

          if (detenido.current) break;
          await esperar(2500);
        }

        if (detenido.current) break;

        // Completar solamente lo que Amazon no encontró.
        aplicarEstimacionesMcoV13(archivos.current[indice]);

        const avanceConservado = Math.max(
          terminadosRef.current, indice + 1
        );
        setArchivosTerminados(avanceConservado);
        terminadosRef.current = avanceConservado;
        await guardarSesionActual();
        actualizar();
      }

      if (!detenido.current) {
        const asins = [...productos.current.keys()];

        const completos = asins.filter(asin =>
          (productos.current.get(asin) || []).every(
            p => completoParaVista(p.mcoLogistics)
          )
        ).length;

        const pendientes = asins.length - completos;

        const mensaje = pendientes
          ? `Proceso terminado: ${completos} completos y ` +
            `${pendientes} pendientes.`
          : `Proceso terminado: ${completos} completos.`;

        setMensaje(mensaje);

        if (pendientes) {
          showWarning(mensaje, {
            title: 'Proceso finalizado con pendientes',
            duration: 12000
          });
        } else {
          showSuccess(mensaje, {
            title: 'Proceso finalizado'
          });
        }
      }

    } catch (error) {
      setMensaje('Error procesando archivos: ' + error.message);
      showError(error.message, {
        title: 'Error de procesamiento'
      });

    } finally {
      activo.current = false;
      archivoActual.current = null;
      setArchivoActivo('');
      setProcesando(false);

      guardarSesionActual().catch(e =>
        showError(e.message, {
          title: 'Error guardando sesión'
        })
      );

      actualizar();
    }
  }

  async function detener() {
    detenido.current = true;
    activo.current = false;
    setProcesando(false);

    try {
      if (lote.current) {
        const resultado = await solicitar('weight-cancel', {
          runId: lote.current
        });

        setMensaje(
          `Detenido. ${resultado.cancelados || 0} trabajos cancelados.`
        );
      }
    } catch (error) {
      setMensaje(
        'Detenido en pantalla, pero no se pudo confirmar ' +
        'la cancelación del VPS: ' + error.message
      );
    }
  }


  // Aplica estimaciones únicamente después de terminar
  // la consulta del i3 para cada ASIN.
  function aplicarEstimacionesMcoV13(archivoUnico = null) {
    const archivosRevisar = archivoUnico
      ? [archivoUnico]
      : archivos.current;

    let calculados = 0;

    for (const archivo of archivosRevisar) {
      if (!archivo) continue;

      for (const producto of archivo.lista || []) {
        const asin = String(
          producto?.asin || producto?.originalAsin || ''
        ).trim().toUpperCase();

        const estado = estados.current.get(asin);

        // No calcular antes de que termine la búsqueda.
        if (!['done', 'failed'].includes(estado)) {
          continue;
        }

        if (estimarMedidasMcoV13(producto)) {
          prepararLogisticaParaPrecios(producto);
          sucios.current.add(archivo.nombre);
          calculados++;
        }
      }
    }

    return calculados;
  }

  // PESO_DESCARGA_JSON_DIRECTO_V14
  async function descargar() {
    if (!archivos.current.length || exportando) return;

    setExportando(true);

    try {
      let cambios = false;

      // Aplicar las estimaciones únicamente a búsquedas terminadas.
      if (!activo.current) {
        const calculados = aplicarEstimacionesMcoV13();
        if (calculados > 0) cambios = true;
      }

      // Compatibilidad con el motor de precios existente.
      for (const archivo of archivos.current) {
        for (const producto of archivo.lista || []) {
          const antesPeso = producto.billableWeightLb;
          const antesMedidas = producto.mercadoLibreDimensions;

          prepararLogisticaParaPrecios(producto);

          if (
            producto.billableWeightLb !== antesPeso ||
            producto.mercadoLibreDimensions !== antesMedidas
          ) {
            sucios.current.add(archivo.nombre);
            cambios = true;
          }
        }
      }

      // PESO_DESCARGA_JSON_ZIP_AUTOMATICO_V15
      // Un JSON: descarga directa.
      // Varios JSON: un unico ZIP.
      const disponibles = archivos.current.filter(
        archivo => archivo && archivo.estructura != null
      );

      if (!disponibles.length) {
        throw new Error('No hay archivos JSON disponibles.');
      }

      const nombreJson = archivo => {
        const original = String(
          archivo.nombre || 'productos.json'
        ).replace(/\\/g, '/').split('/').pop();

        return /\.json$/i.test(original)
          ? original
          : `${original}.json`;
      };

      let blob;
      let nombreDescarga;

      if (disponibles.length === 1) {
        const archivo = disponibles[0];

        blob = new Blob(
          [JSON.stringify(archivo.estructura, null, 2)],
          { type: 'application/json;charset=utf-8' }
        );

        nombreDescarga = nombreJson(archivo);

      } else {
        const zip = new JSZip();
        const nombresUsados = new Set();

        for (const archivo of disponibles) {
          const nombre = nombreJson(archivo);

          let ruta = nombre;
          let numero = 2;

          // Evitar sobrescribir archivos con nombres repetidos.
          while (nombresUsados.has(ruta.toLowerCase())) {
            ruta = `duplicado-${numero}/${nombre}`;
            numero++;
          }

          nombresUsados.add(ruta.toLowerCase());

          zip.file(
            ruta,
            JSON.stringify(archivo.estructura, null, 2)
          );
        }

        blob = await zip.generateAsync({
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: { level: 6 }
        });

        nombreDescarga = 'MercadoBulk_JSON_corregidos.zip';
      }

      const url = URL.createObjectURL(blob);
      const enlace = document.createElement('a');

      enlace.href = url;
      enlace.download = nombreDescarga;

      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();

      window.setTimeout(
        () => URL.revokeObjectURL(url),
        60000
      );

      const descargados = disponibles.length;

      if (cambios) {
        await guardarSesionActual();
      }

      setMensaje(
        descargados === 1
          ? '1 archivo JSON descargado.'
          : `${descargados} archivos JSON descargados en un solo ZIP.`
      );

      actualizar();

    } catch (error) {
      setMensaje('Error descargando JSON: ' + error.message);

      showError(error.message, {
        title: 'Error descargando JSON'
      });

    } finally {
      setExportando(false);
    }
  }


  // PESO_UI_PRIMERA_IMAGEN_V2: presentación solamente.
  function foto(p) {
    for (const v of [
      p.highResolutionImages?.[0],
      p.images?.[0],
      p.pictures?.[0],
      p.imageUrls?.[0],
      p.mainImage,
      p.imageUrl,
      p['imagen en miniatura'],
      p['Imágenes']
    ]) {
      const url = String(
        typeof v === 'string' ? v : (v?.url || v?.src || '')
      ).split(',')[0].trim();

      if (/^https?:\/\//i.test(url)) return url;
    }
    return null;
  }

  const filas = [];
  let total = 0, completos = 0;

  for (const a of archivos.current) {
    for (let i = 0; i < a.lista.length; i++) {
      const p = a.lista[i];
      const ok = completoParaVista(p.mcoLogistics);

      total++;
      if (ok) completos++;

      if (
        (filtro === 'completos' && !ok) ||
        (filtro === 'pendientes' && ok)
      ) continue;

      if (
        !`${asinDe(p)} ${p.title || ''}`
          .toLowerCase()
          .includes(busqueda.toLowerCase())
      ) continue;

      filas.push({p, id: `${a.nombre}:${i}`});
    }
  }

  const nAsins = productos.current.size;
  const finalizados = [...productos.current.keys()].filter(
    asin => ['done', 'completo', 'failed', 'cancelled'].includes(
      estados.current.get(asin)
    )
  ).length;

  // El avance acumulado no se pierde por reintentos.
  const registrados = new Set(atendidosHistoricos.current);

  for (const [asin, estado] of estados.current) {
    if (['done', 'completo', 'failed', 'cancelled']
        .includes(estado)) {
      registrados.add(asin);
    }
  }

  const atendidos = [...registrados].filter(
    asin => productos.current.has(asin)
  ).length;

  const progreso = nAsins
    ? Math.min(100, Math.round(atendidos * 100 / nAsins))
    : 0;

  const paginas = Math.max(1, Math.ceil(filas.length / 10));
  const actual = Math.max(1, Math.min(pagina, paginas));
  const visibles = filas.slice((actual - 1) * 10, actual * 10);

  // PESO_FINALIZACION_CLARA_V5
  const paquetesIncompletos = Math.max(0, total - completos);
  const ejecucionFinalizada =
    !procesando &&
    enviados > 0 &&
    nAsins > 0 &&
    finalizados >= nAsins;

  const todoCompleto =
    ejecucionFinalizada && paquetesIncompletos === 0;

  const tarjetas = [
    ['Archivos', archivos.current.length, 'Archivos cargados', '▤'],
    ['Productos', total, 'Productos originales', '⬡'],
    ['Enviados', enviados, 'ASIN enviados al i3', '↗'],
    ['Con peso y dimensiones', completos, 'Peso y dimensiones disponibles', '✓']
  ];

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-5 text-foreground">

      <header className="flex items-center gap-3">
        <div className="h-12 w-12 flex items-center justify-center bg-blue-600 text-white rounded-xl shrink-0">
          <Icon name="Truck" size={28} />
        </div>

        <div>
          <h1 className="text-2xl font-bold">Peso y dimensiones</h1>

        </div>
      </header>

      <section className="bg-card rounded-2xl border border-border shadow-sm p-4 md:p-5 space-y-3">
        <h2 className="font-semibold">Archivos de entrada (JSON o ZIP)</h2>

        <div className="flex flex-col xl:flex-row gap-3 xl:items-center">

          <div
            className="flex-1 border-2 border-dashed border-border bg-muted/20 rounded-xl p-5"
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              if (e.dataTransfer.files.length) {
                cargar({
                  target: {
                    files: e.dataTransfer.files,
                    value: ''
                  }
                });
              }
            }}
          >
            <div className="text-sm font-medium">
              Arrastra tus archivos aquí o haz clic para seleccionar
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-3">
              <input
                id="entrada-pesos"
                type="file"
                disabled={cargando}
                multiple
                accept=".json,.zip"
                onChange={cargar}
                className="sr-only"
              />

              <label
                htmlFor="entrada-pesos"
                className={`bg-blue-50 text-blue-700 border border-blue-300 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer `}
              >
                {archivos.current.length
                  ? 'Añadir archivos'
                  : 'Elegir archivos'}
              </label>

              <span className="text-sm text-muted-foreground">
                {archivos.current.length
                  ? `${archivos.current.length} archivos cargados`
                  : 'Ningún archivo seleccionado'}
              </span>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap xl:justify-end">
            <button
              onClick={iniciar}
              disabled={procesando || cargando || !nAsins}
              className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-3 disabled:opacity-40"
            >
              ▶ Iniciar
            </button>

            <button
              onClick={detener}
              disabled={!procesando}
              className="rounded-lg border border-border px-5 py-3 font-semibold disabled:opacity-40"
            >
              ■ Detener
            </button>

            <button
              type="button"
              onClick={() => recargarSesion(false)}
              disabled={procesando || !sesionLista}
              className="rounded-lg border border-border px-4 py-3 font-semibold disabled:opacity-40"
            >
              Recargar sesión
            </button>

            <button
              type="button"
              onClick={nuevoLote}
              disabled={procesando}
              className="rounded-lg border border-border px-4 py-3 font-semibold disabled:opacity-40"
            >
              Nuevo lote
            </button>

            <button
              onClick={descargar}
              disabled={!total || exportando}
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3 disabled:opacity-40"
            >
              {exportando ? 'Generando...' : '↓ Descargar JSON'}
            </button>
          </div>
        </div>

        {(!procesando ||
          !mensaje.startsWith('Procesando archivo')) && (
          <p role="status" className="text-sm text-muted-foreground">
            {mensaje}
          </p>
        )}
      </section>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {tarjetas.map(([nombre, valor, detalle, icono], i) => (
          <div
            key={nombre}
            className="bg-card border border-border rounded-2xl shadow-sm p-4 flex items-center gap-3"
          >
            <div className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center text-xl font-bold ${
              i === 1 || i === 3
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-blue-50 text-blue-700'
            }`}>
              {icono}
            </div>

            <div>
              <div className="text-xs text-muted-foreground font-semibold">
                {nombre}
              </div>
              <div className="text-2xl font-bold tabular-nums">
                {Number(valor).toLocaleString('es-CO')}
              </div>
              <div className="text-xs text-muted-foreground">
                {detalle}
              </div>
            </div>
          </div>
        ))}
      </div>

      <section className="bg-card border border-border shadow-sm rounded-2xl p-4 md:p-5">
        <div className="flex justify-between flex-wrap gap-2 mb-4">
          <h2 className="font-semibold">
            {procesando
              ? 'Recuperación en curso'
              : ejecucionFinalizada
                ? todoCompleto
                  ? 'Proceso finalizado correctamente'
                  : 'Proceso finalizado con datos pendientes'
                : 'Estado de recuperación'}
          </h2>

          <span className="text-sm text-muted-foreground">
            {atendidos} / {nAsins} ASIN con avance registrado
          </span>
        </div>

        <div className="flex items-center gap-5">
          <div
            className="h-20 w-20 rounded-full shrink-0 flex items-center justify-center"
            style={{
              background: `conic-gradient(#2563eb ${progreso}%,#e2e8f0 0)`
            }}
          >
            <span className="bg-card rounded-full h-16 w-16 flex items-center justify-center font-bold text-lg">
              {`${progreso}%`}
            </span>
          </div>

          <div className="flex-1 space-y-2 min-w-0">
            {archivoActivo && (
              <p className="text-sm font-semibold text-blue-600">
                {archivoActivo} · {archivosTerminados} de
                {' '}{archivos.current.length} archivos terminados
              </p>
            )}

            <div className="h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all rounded-full"
                style={{width: `${progreso}%`}}
              />
            </div>

            <p className="text-xs text-muted-foreground">
              {completos} productos con peso y dimensiones recuperados
            </p>
          </div>
        </div>
      </section>

      {ejecucionFinalizada && (
        <div
          role="status"
          aria-live="polite"
          className={`rounded-xl border p-4 ${
            todoCompleto
              ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
              : 'border-amber-300 bg-amber-50 text-amber-950'
          }`}
        >
          <div className="font-bold text-base">
            {todoCompleto
              ? '✓ Proceso finalizado correctamente'
              : '⚠ Proceso finalizado con datos pendientes'}
          </div>
          <p className="text-sm mt-1">
            {finalizados} de {nAsins} consultas terminadas.
            {' '}{completos} productos con paquete completo.
            {' '}{paquetesIncompletos} productos con datos logísticos pendientes.
          </p>
          
        </div>
      )}

      <section className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden">

        <div className="p-4 md:p-5 flex flex-wrap gap-3 justify-between items-center">
          <div>
            <h2 className="font-semibold">Vista previa de resultados</h2>
          </div>

          <div className="flex gap-2 flex-wrap">
            <select
              value={filtro}
              onChange={e => {
                setFiltro(e.target.value);
                setPagina(1);
              }}
              className="rounded-lg bg-background border border-border px-3 py-2 text-sm"
            >
              <option value="todos">Todos</option>
              <option value="completos">Paquete completo</option>
              <option value="pendientes">Paquete pendiente</option>
            </select>

            <input
              value={busqueda}
              onChange={e => {
                setBusqueda(e.target.value);
                setPagina(1);
              }}
              placeholder="Buscar título o ASIN..."
              className="rounded-lg bg-background border border-border px-3 py-2 text-sm w-56 max-w-full"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left min-w-[990px]">
            <thead className="bg-muted/40 text-muted-foreground border-y border-border">
              <tr>
                {[
                  'Sel.',
                  'Imagen',
                  'Título',
                  'ASIN',
                  'Peso',
                  'Dimensiones',
                  'Fuente',
                  'Estado'
                ].map(t => (
                  <th key={t} className="p-3">{t}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {visibles.map(({p, id}) => {
                const l = p.mcoLogistics || {};
                const asin = asinDe(p);
                const img = foto(p);
                const ok = completoParaVista(l);
                const estado = estados.current.get(asin);

                const pp = l.pesoPaquete?.grams > 0;
                const dp = l.dimensionesPaquete?.lengthCm > 0;
                const ap = l.pesoArticulo?.grams > 0;
                const ad = l.dimensionesArticulo?.lengthCm > 0;

                const fuente = pp && dp
                  ? 'Paquete'
                  : (pp || dp) && (ap || ad)
                    ? 'Mixta'
                    : pp || dp
                      ? 'Paquete'
                      : ap || ad
                        ? 'Artículo'
                        : '—';

                const texto = ok
                  ? 'Completo'
                  : estado === 'done'
                    ? 'Parcial'
                    : estado === 'failed'
                      ? 'No recuperado'
                      : estado === 'leased'
                        ? 'Recuperando'
                        : estado === 'pending'
                          ? 'En cola'
                          : estado === 'cancelled'
                            ? 'Cancelado'
                            : 'Pendiente';

                return (
                  <tr key={id} className="border-b border-border hover:bg-muted/20">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={seleccionados.includes(id)}
                        aria-label={`Seleccionar ${asin}`}
                        onChange={e => setSeleccionados(prev =>
                          e.target.checked
                            ? [...prev, id]
                            : prev.filter(x => x !== id)
                        )}
                      />
                    </td>

                    <td className="p-3">
                      <button
                        type="button"
                        disabled={!img}
                        title={img ? 'Haz clic para ampliar la fotografía' : 'Sin imagen'}
                        onClick={() => img && setImagenAmpliada({
                          src: img,
                          titulo: p.title || 'Producto'
                        })}
                        className="w-20 h-20 bg-muted/30 border border-border rounded-xl flex items-center justify-center overflow-hidden disabled:cursor-default cursor-zoom-in hover:border-blue-500 transition-colors"
                      >
                        {img ? (
                          <img
                            src={img}
                            alt={p.title || 'Producto'}
                            loading="lazy"
                            className="w-full h-full object-contain"
                            onError={e => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className="text-muted-foreground">▧</span>
                        )}
                      </button>
                    </td>

                    <td className="p-3 font-medium max-w-xs">
                      {p.title || 'Sin título'}
                    </td>

                    <td className="p-3 text-xs font-mono whitespace-nowrap">
                      {/^[A-Z0-9]{10}$/.test(asin) ? (
                        <a
                          href={`https://www.amazon.com/dp/${asin}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline hover:text-blue-800"
                          title="Ver producto en Amazon"
                        >
                          {asin} ↗
                        </a>
                      ) : '—'}
                    </td>

                    <td className="p-3 whitespace-nowrap">
                      {peso(l.pesoPaquete || l.pesoArticulo)}
                    </td>

                    <td className="p-3 whitespace-nowrap">
                      {dimensiones(l.dimensionesPaquete || l.dimensionesArticulo)}
                    </td>

                    <td className="p-3">
                      <span className="rounded-md bg-muted px-2 py-1 text-xs">
                        {fuente}
                      </span>
                    </td>

                    <td className={`p-3 font-semibold whitespace-nowrap ${
                      ok ? 'text-emerald-600' : 'text-amber-600'
                    }`}>
                      {texto}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!visibles.length && (
            <div className="text-center text-muted-foreground text-sm p-10">
              {total
                ? 'No hay resultados con ese filtro.'
                : 'Carga archivos para ver aquí fotos, títulos y medidas.'}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border flex flex-wrap justify-between gap-3 text-sm items-center">
          <span className="text-muted-foreground">
            Mostrando {filas.length ? (actual - 1) * 10 + 1 : 0}–
            {Math.min(actual * 10, filas.length)} de {filas.length} productos
          </span>

          <div className="flex items-center gap-2">
            <button
              disabled={actual <= 1}
              onClick={() => setPagina(actual - 1)}
              className="px-3 py-2 border border-border rounded-lg disabled:opacity-40"
            >
              Anterior
            </button>

            <span>{actual} / {paginas}</span>

            <button
              disabled={actual >= paginas}
              onClick={() => setPagina(actual + 1)}
              className="px-3 py-2 border border-border rounded-lg disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        </div>
      </section>

      {imagenAmpliada && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setImagenAmpliada(null)}
          role="presentation"
        >
          <div
            className="bg-card text-foreground rounded-2xl shadow-2xl w-full max-w-4xl p-4"
            onClick={e => e.stopPropagation()}
            role="presentation"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <h3 className="font-semibold text-base">
                {imagenAmpliada.titulo}
              </h3>
              <button
                type="button"
                onClick={() => setImagenAmpliada(null)}
                className="text-xl font-bold border border-border rounded-lg px-3 py-1"
                aria-label="Cerrar imagen"
              >
                ×
              </button>
            </div>
            <img
              src={imagenAmpliada.src}
              alt={imagenAmpliada.titulo}
              className="max-h-[75vh] w-full object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
}
